//@ts-check
'use strict';

/**
 * Odds Guide — card carousel (CDN-1869).
 *
 * On mobile the per-group card row (`.odds-guide-cards`) becomes a horizontal
 * scroll-snap carousel: one card per viewport with pagination dots and
 * prev/next arrows. On desktop the same markup is a static grid (CSS-driven),
 * so this script is effectively inert there — it only reacts while the track
 * actually overflows.
 *
 * Minimal, dependency-free, namespaced; a no-op when no carousels are present.
 * Built bespoke (rather than reusing GenericCarousel) because the cards are
 * fluid full-width on mobile — GenericCarousel requires fixed pixel widths.
 */
(function () {
	/** @type {HTMLElement[]} */
	const carousels = Array.from(document.querySelectorAll('[data-odds-guide-carousel]'));
	if (!carousels.length) {
		return;
	}

	carousels.forEach((carousel) => {
		/** @type {HTMLElement | null} */
		const track = carousel.querySelector('.odds-guide-cards__track');
		if (!track) {
			return;
		}

		/** @type {HTMLElement[]} */
		const cards = Array.from(track.children).filter(
			(el) => el instanceof HTMLElement
		);
		if (cards.length < 2) {
			return; // single card: no carousel chrome needed.
		}

		const dotsContainer = carousel.querySelector('.odds-guide-cards__dots');
		const prevBtn = carousel.querySelector('.odds-guide-cards__arrow--prev');
		const nextBtn = carousel.querySelector('.odds-guide-cards__arrow--next');

		// --- Build dots --------------------------------------------------------
		/** @type {HTMLButtonElement[]} */
		const dots = [];
		if (dotsContainer) {
			dotsContainer.innerHTML = '';
			cards.forEach((_, i) => {
				const dot = document.createElement('button');
				dot.type = 'button';
				dot.className = 'odds-guide-cards__dot';
				// Decorative pagination: the controls wrapper is aria-hidden and
				// the cards themselves are the accessible navigation, so keep the
				// dots out of the tab order and out of the accessibility tree.
				dot.tabIndex = -1;
				dot.setAttribute('aria-hidden', 'true');
				dot.addEventListener('click', () => scrollToIndex(i));
				dotsContainer.appendChild(dot);
				dots.push(dot);
			});
		}

		let currentIndex = 0;

		/**
		 * Whether the carousel is currently in its scrolling (overflowing)
		 * state — i.e. mobile. On desktop the grid doesn't overflow.
		 * @returns {boolean}
		 */
		const isActive = () => track.scrollWidth > track.clientWidth + 2;

		/**
		 * Scroll the track so card #index is aligned to the start.
		 * @param {number} index
		 */
		function scrollToIndex(index) {
			const clamped = Math.max(0, Math.min(cards.length - 1, index));
			const card = cards[clamped];
			if (!card) {
				return;
			}
			track.scrollTo({ left: card.offsetLeft - track.offsetLeft, behavior: 'smooth' });
		}

		/**
		 * Find the card whose left edge is closest to the track's scroll
		 * position, update dots + arrow disabled states.
		 */
		function syncState() {
			const active = isActive();
			carousel.classList.toggle('is-carousel', active);

			let nearest = 0;
			let min = Infinity;
			const scrollLeft = track.scrollLeft;
			cards.forEach((card, i) => {
				const dist = Math.abs((card.offsetLeft - track.offsetLeft) - scrollLeft);
				if (dist < min) {
					min = dist;
					nearest = i;
				}
			});
			currentIndex = nearest;

			dots.forEach((dot, i) => {
				dot.classList.toggle('is-active', i === nearest);
			});

			if (prevBtn) {
				prevBtn.toggleAttribute('disabled', nearest <= 0);
			}
			if (nextBtn) {
				nextBtn.toggleAttribute('disabled', nearest >= cards.length - 1);
			}
		}

		if (prevBtn) {
			prevBtn.addEventListener('click', () => scrollToIndex(currentIndex - 1));
		}
		if (nextBtn) {
			nextBtn.addEventListener('click', () => scrollToIndex(currentIndex + 1));
		}

		// Debounced scroll handler via rAF.
		let ticking = false;
		track.addEventListener(
			'scroll',
			() => {
				if (ticking) {
					return;
				}
				ticking = true;
				window.requestAnimationFrame(() => {
					syncState();
					ticking = false;
				});
			},
			{ passive: true }
		);

		window.addEventListener('resize', syncState, { passive: true });
		syncState();
	});
})();
